import { useState, ReactNode } from 'react';
import { Dialog, DialogTitle, DialogActions, CircularProgress, Box } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { H2, Subtitle1 } from '../../components/typography/typography';
import { PrimaryButton, SecondaryButton } from '../../components/buttons/buttons';
import { useTranslation } from 'react-i18next';

export type ConfirmVariant = 'warning' | 'danger' | 'success' | 'info';

const VARIANT_CONFIG: Record<ConfirmVariant, { icon: React.ComponentType<{ sx?: any }>; color: string; buttonSx: any }> = {
  warning: { icon: WarningIcon, color: 'warning.main', buttonSx: { backgroundColor: 'warning.main', '&:hover': { backgroundColor: 'warning.dark' } } },
  danger: { icon: ErrorOutlineIcon, color: 'error.main', buttonSx: { backgroundColor: 'error.main', '&:hover': { backgroundColor: 'error.dark' } } },
  success: { icon: CheckCircleOutlineIcon, color: 'success.main', buttonSx: { backgroundColor: 'success.main', '&:hover': { backgroundColor: 'success.dark' } } },
  info: { icon: InfoOutlinedIcon, color: 'info.main', buttonSx: { backgroundColor: 'info.main', '&:hover': { backgroundColor: 'info.dark' } } },
};

const useConfirm = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState<ReactNode>('');
  const [confirmText, setConfirmText] = useState('');
  const [variant, setVariant] = useState<ConfirmVariant | null>(null);
  const [resolver, setResolver] = useState<((value: boolean) => void) | null>(null);

  const confirm = () => new Promise<boolean>(resolve => {
    setIsOpen(true);
    setIsLoading(false);
    setResolver(() => resolve);
  });

  const handleConfirm = () => {
    if (resolver) resolver(true);
    setIsLoading(true);
  };

  const handleClose = (_event: any, reason: string) => {
    // Allow closing on backdrop click and escape key
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      setIsOpen(false);
      if (resolver) resolver(false);
    }
  };

  const ConfirmModal = (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      PaperProps={{
        sx: {
          backgroundColor: 'background.white',
          borderRadius: 2,
          padding: 2,
          border: '1px solid rgba(0, 0, 0, 0.1)',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(30, 30, 30, 0.75)',
          backdropFilter: 'blur(4px)',
        },
      }}>

      <DialogTitle sx={{ fontSize: (theme: any) => theme.typography.fontSizes.heading2, fontWeight: 600, color: 'text.darkGrey', textAlign: 'center' }}>
        {variant && (() => {
          const VariantIcon = VARIANT_CONFIG[variant].icon;
          return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
              <VariantIcon sx={{ color: VARIANT_CONFIG[variant].color, fontSize: 40 }} />
            </Box>
          );
        })()}
        <H2>{title}</H2>
        <Subtitle1 sx={{ mt: 1.5 }}>{description}</Subtitle1>
      </DialogTitle>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <SecondaryButton fullWidth onClick={() => { setIsOpen(false); resolver?.(false); }}> {t('Cancel')} </SecondaryButton>
        <PrimaryButton fullWidth onClick={() => handleConfirm()} sx={variant ? VARIANT_CONFIG[variant].buttonSx : undefined}> { isLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : t(confirmText) } </PrimaryButton>
      </DialogActions>
    </Dialog>
  );

  return {
    setConfirmTitle: setTitle,
    setConfirmDescription: setDescription,
    setConfirmVariant: setVariant,
    setConfirmText: setConfirmText,
    confirm, 
    setConfirmIsOpen: setIsOpen,
    ConfirmModal
  };
};

export default useConfirm;
  